import {  FormikValues } from "formik";
import {
  getBusinessDocuments,
  getCountryList,
  getJuridicalActivities,
  getPhysicalActivities,
  sendStep,
} from "../../connectors/connectors";
import {
  smallStringRequired,
  checkboxRequired,
  checkbox,
} from "../../validations";
import {
  Option,
  PitbullActivities,
  PitbullCountries,
  Field,
  ComponentStep,
  PersonType,
  PreconfirmationStep,
  PitbullCountry,
  PitbullActivityJuridical,
  ShareholderData,
  DocumentBusinessType,
  PitbullActivityPhysical,
  DocumentMetadata,
  DraftUUID,
  Template,
  StepSixPJ,
} from "../../types";
import juridicalIcon from "../../assets/images/icons/ui/juridical.svg";
import {
  capitalizeString,
  getDraftStepsToSend,
  getProvincesByIso,
  hasExpiredToken,
  mapDocumentBusinessTypeFromValue,
} from "../../app/utils";
import emailValidationJuridicalStep, {
  getEmailVerificationHandlers,
  getQuestions,
} from "../../steps/juridical/emailValidationJuridicalStep";
import {
  onUpdateFormData,
} from "../../reducers/onboardingDataReducer";
import {
  asCuit,
  getCitizenshipOptions,
  getCountriesOptions,
  getFormattedPreconfirmationFields,
  getOnboardingFields,
  getPreconfirmationStepFieldsByName,
  getUnificationParams,
  isAllariaMasOnboarding,
  isCompanyWithoutShareholders,
  isUnifiedOnboarding,
  isAgrofinanzasOnboarding,
  isAllariaFondosOnboarding,
  isIntervalores,
} from "../../utils";
import basicInformationJuridicalStep from "../../steps/juridical/basicInformationJuridicalStep";
import representativeStep from "../../steps/juridical/representativeStep";
import entityStep from "../../steps/juridical/entityStep";
import shareholdersStep from "../../steps/juridical/shareholdersStep";
import investmentProfileJuridicalStep from "../../steps/juridical/investmentProfileJuridicalStep";
import juridicalDocumentationStep from "../../steps/juridical/juridicalDocumentationStep";
import infoJuridicalStep from "../../steps/juridical/infoJuridicalStep";
import * as Sentry from "@sentry/react";
import { AppDispatch, RootState } from "../../app/store";
import { getCompanyData } from "../../utils/companies";
import identityJuridicalStep from "../../steps/juridical/identityJuridicalStep";

const getActivitiesPJ = async (): Promise<PitbullActivities> => {
  try {
    const { data } = await getJuridicalActivities();
    return data;
  } catch (error) {
    throw new Error("Error al obtener las actividades");
  }
};

const getActivitiesPF = async (): Promise<PitbullActivities> => {
  try {
    const { data } = await getPhysicalActivities();
    return data;
  } catch (error) {
    throw new Error("Error al obtener las actividades");
  }
};

const getCountries = async (): Promise<PitbullCountries> => {
  try {
    const { data } = await getCountryList();
    return data;
  } catch (error) {
    throw new Error("Error al obtener los países");
  }
};

const getDocuments = async (
  draftUUID?: string,
): Promise<DocumentMetadata[] | undefined> => {
  //With an expired token it fails and stops the template from loading
  if (!draftUUID || hasExpiredToken()) return;

  const response = await getBusinessDocuments({ draftUUID });
  return response?.data;
};

const onboardingJuridical = async (
  draftUUID: DraftUUID | undefined,
  dispatch: AppDispatch,
): Promise<Template> => {
  try {
    const arrayData = await Promise.all([
      getActivitiesPJ(),
      getCountries(),
      getActivitiesPF(),
      getDocuments(draftUUID),
    ]);

    // Not destructuring to avoid type errors
    const juridicalActivities = arrayData[0] as PitbullActivityJuridical[];
    const countries = arrayData[1] as PitbullCountry[];
    const physicalActivities = arrayData[2] as PitbullActivityPhysical[];
    const businessDocuments = arrayData[3] as DocumentMetadata[] | undefined;

    const businessDocumentsByType = await (async () => {
      const docs = businessDocuments?.filter(
        ({ type }) => !["SELFIE", "DNI_BACK", "DNI_FRONT"].includes(type),
      );

      const acc: Record<string, DocumentMetadata[]> = {};

      if (!docs) return null;

      for (const doc of docs) {
        const type = doc.type as DocumentBusinessType;
        const parsedType = mapDocumentBusinessTypeFromValue(type);
        try {
          if (!acc[parsedType]) {
            acc[parsedType] = [];
          }
          // Store the full metadata object, not just the filename
          acc[parsedType].push(doc);
        } catch (e) {
          console.error("Failed to process document metadata for", doc, e);
        }
      }
      return acc;
    })();

    if (businessDocumentsByType) {
      dispatch(
        onUpdateFormData({
          businessDocumentation: businessDocumentsByType,
        }),
      );
    }

    const countriesTyped = countries as PitbullCountry[];

    const activitiesOptionsPJ: Option[] = (
      juridicalActivities as PitbullActivityJuridical[]
    )?.map(({ activity: { name } }) => {
      return { value: name, label: name };
    });

    const activitiesOptionsPF: Option[] = (
      physicalActivities as PitbullActivityPhysical[]
    )?.map(({ name }) => {
      return { value: name, label: name };
    });

    const countriesOptions = getCountriesOptions(countriesTyped);

    const citizenshipOptions = getCitizenshipOptions(countriesTyped);

    const provinces = await getProvincesByIso();

    const getCountryIsoAlpha2 = (
      countries: PitbullCountry[],
      country: string,
    ) =>
      countries.find(
        ({ name }) =>
          name.localeCompare(country, "es", {
            sensitivity: "base", // Ignore accents and case
          }) === 0,
      )?.isoAlpha2;

    const getProvincesFilteredByCountry = (country = "Argentina") => {
      const iso = getCountryIsoAlpha2(countries, country);
      const filteredProvince = provinces.filter((prov) => {
        if (prov.countryIsoAlpha2 === iso) {
          return prov;
        }
      });
      return filteredProvince;
    };

    const questions = await getQuestions();

    const investmentProfileStep = investmentProfileJuridicalStep(questions);

    const juridicalSteps = [
      basicInformationJuridicalStep(),
      emailValidationJuridicalStep({ step: 2 }),
      identityJuridicalStep(countriesTyped),
      representativeStep(
        countriesOptions,
        activitiesOptionsPF,
        citizenshipOptions,
        getProvincesFilteredByCountry,
        provinces,
      ),
      entityStep(
        countriesOptions,
        activitiesOptionsPJ,
        getProvincesFilteredByCountry,
        provinces,
      ),
      shareholdersStep(
        countriesOptions,
        activitiesOptionsPJ,
        citizenshipOptions,
        activitiesOptionsPF,
        getProvincesFilteredByCountry,
        provinces,
      ),
      investmentProfileStep,
      juridicalDocumentationStep(businessDocumentsByType),
    ];

    const onboardingSteps = [
      ...juridicalSteps.map((step, index) => ({ step: index + 1, ...step })),
      infoJuridicalStep(),
    ];

    const onboardingFields = getOnboardingFields(onboardingSteps);
    const formattedOnboardingFields =
      getPreconfirmationStepFieldsByName(onboardingFields);

    const fieldsNamesInRepresentativePreconfirmationStep = [
      "cuit",
      "name",
      "lastName",
      "citizenship",
      "birthDate",
      "civilStatus",
      "spouseName",
      "spouseLastName",
      "spouseCuil",
      "telephone",
      "activities",
      "activitiesDetails",
      "representativesAddresses.country",
      "representativesAddresses.fullAddress",
      "representativesAddresses.floor",
      "representativesAddresses.apartment",
      "representativesAddresses.zipCode",
      "representativesAddresses.municipality",
      "representativesAddresses.state",
      "pep",
      "pepDetails",
    ];

    const representativePreconfirmationStep: PreconfirmationStep = {
      name: "Datos del representante",
      columns: 4,
      fields: [
        ...fieldsNamesInRepresentativePreconfirmationStep.map(
          (field) => formattedOnboardingFields[field],
        ),
      ],
      editStep: {
        step: 4,
        substep: 1,
      },
    };

    const fieldsNamesInBusinessPreconfirmationStep = [
      "businessInformation.legalName",
      "businessInformation.cuit",
      "entityAddress.fullAddress",
      "entityAddress.floor",
      "entityAddress.apartment",
      "entityAddress.zipCode",
      "entityAddress.municipality",
      "entityAddress.state",
      "businessInformation.corporateType",
      "businessInformation.activities",
      "businessInformation.activitiesDetails",
      "businessInformation.registrationCountry",
      "businessInformation.registrationDate",
      "businessInformation.registrationNumber",
      "businessInformation.registrationAuthority",
      "businessInformation.registrationAuthorityDetails",
      "businessInformation.hasJointSignature",
      "businessInformation.isPublicEntity",
      "businessInformation.isInEconomicGroup",
      "businessInformation.bankAccounts",
      "businessInformation.foreignTaxDetailBool",
      "businessInformation.foreignTaxDetail.country",
      "businessInformation.foreignTaxDetail.idNumber",
    ];

    const businessPreconfirmationStep: PreconfirmationStep = {
      name: "Datos de la entidad",
      columns: 4,
      fields: [
        ...fieldsNamesInBusinessPreconfirmationStep.map(
          (field) => formattedOnboardingFields[field],
        ),
      ],
      editStep: {
        step: 5,
        substep: 1,
      },
    };

    const signatoriesPreconfirmationStep = ({
      values,
    }: {
      values: FormikValues;
    }): PreconfirmationStep => {
      const signatories = values?.signatories || [];
      const representativeIdNumber = values?.identityDocument || {};

      const signatoriesStep = entityStep(
        countriesOptions,
        activitiesOptionsPJ,
        getProvincesFilteredByCountry,
        provinces,
      ).subSteps?.find(
        (subStep) => "secondaryForm" in subStep && !!subStep.secondaryForm,
      ) as ComponentStep;
      const fieldsFromStep = signatoriesStep.secondaryForm?.fields || [];
      const excludedFields = ["name", "lastName", "confirmEmail"];

      const fields = fieldsFromStep.filter(
        (field) => !excludedFields.includes(field.name),
      );

      const signatoriesPreconfirmationFields = Array.from(
        { length: signatories.length },
        (_, i) => {
          const signatoryFields: Field[] = [
            {
              name: "fullName",
              type: "text",
              label: `Firmante ${i + 1}`,
              validation: smallStringRequired,
              initialValue: "",
              columns: 2,
              valueGetter: (values) =>
                `${values.name} ${capitalizeString(values.lastName)} ${
                  representativeIdNumber === values.idNumber ? "(Tú)" : ""
                }`,
            },
            ...fields,
          ];

          return getFormattedPreconfirmationFields(
            signatoryFields,
            signatories[i],
          );
        },
      );

      return {
        name: `Firmantes (${signatories.length})`,
        columns: 3,
        fieldsWithSeparator: signatoriesPreconfirmationFields,
        editStep: {
          step: 5,
          substep: Number(signatoriesStep.step),
        },
        hideStep: values.businessInformation.hasJointSignature === "NO",
      };
    };

    const getFieldsByShareholder = (
      shareholders: ShareholderData[],
      cuitFromRepresentative: string,
    ) => {
      const fields =
        shareholdersStep(
          countriesOptions,
          activitiesOptionsPJ,
          citizenshipOptions,
          activitiesOptionsPF,
          getProvincesFilteredByCountry,
          provinces,
        ).secondaryForm?.fields || [];

      const excludedFields = ["name", "lastName", "legalName"];

      let shareholdersFields = fields.filter(
        (field) => !excludedFields.includes(field.name),
      );
      const shareholderFieldsWithoutDuplicates = new Set(
        shareholdersFields.map((field) => field.name),
      );
      shareholdersFields = Array.from(shareholderFieldsWithoutDuplicates).map(
        (name) =>
          shareholdersFields.find((field) => field.name === name) as Field,
      );

      // Get fields for each shareholder and where to get their values
      return Array.from({ length: shareholders.length }, (_, i) =>
        getFormattedPreconfirmationFields(
          [
            {
              name: "fullName",
              type: "text",
              label: `Accionista ${i + 1}`,
              validation: smallStringRequired,
              initialValue: "",
              columns: 2,
              valueGetter: (values) => {
                return values.personType === "JURIDICAL"
                  ? values.legalName
                  : `${values.name} ${capitalizeString(values.lastName)} ${
                      cuitFromRepresentative === values.cuit ? "(Tú)" : ""
                    }`;
              },
            },
            ...shareholdersFields,
          ],
          shareholders[i],
        ),
      );
    };

    const shareholdersPreconfirmationStep = ({
      values,
    }: {
      values: FormikValues;
    }): PreconfirmationStep => {
      const cuitFromRepresentative = values?.cuit || {};
      const shareholders = values?.shareholders || [];

      return {
        name: `Accionistas (${shareholders.length})`,
        columns: 4,
        fieldsWithSeparator: getFieldsByShareholder(
          shareholders,
          cuitFromRepresentative,
        ),
        editStep: { step: 6 },
        hideStep: isCompanyWithoutShareholders(
          values?.businessInformation?.corporateType,
        ),
      };
    };

    const investmentProfilePreconfirmationStep: PreconfirmationStep = {
      name: "Perfil inversor",
      columns: 2,
      fields: getFormattedPreconfirmationFields(investmentProfileStep.fields ?? []),
      editStep: {
        step: 7,
      },
    };

    const documentationPreconfirmationStep: PreconfirmationStep = {
      name: "Documentación",
      columns: 2,
      fields: getFormattedPreconfirmationFields(
        juridicalDocumentationStep(businessDocumentsByType).fields || [],
      ),
      editStep: {
        step: 8,
      },
    };

    const unificationPreconfirmationStep: PreconfirmationStep = {
      name: "Unificación de cuentas",
      columns: 4,
      fields: [
        {
          name: "company",
          label: "Empresa",
          type: "text",
          valueGetter: () => "Allaria",
          isConditional: false,
        },
        {
          name: "accountId",
          label: "Número de cuenta",
          type: "cuit",
          valueGetter: () => `#${getUnificationParams().accountId}`,
          isConditional: false,
        },
        {
          name: "cuit",
          label: "CUIT / CUIL",
          type: "cuit",
          valueGetter: () => asCuit(getUnificationParams().cuit),
          isConditional: false,
        },
        {
          name: "accountName",
          label: "Razón Social",
          type: "cuit",
          valueGetter: () => getUnificationParams().accountName,
          isConditional: false,
        },
      ],
    };

    const preconfirmationSteps = ({
      values,
    }: {
      values?: FormikValues | null;
    }): PreconfirmationStep[] => {
      if (!values) throw new Error("Error: values not found");

      return [
        ...(isUnifiedOnboarding() && isAllariaMasOnboarding()
          ? [unificationPreconfirmationStep]
          : []),
        representativePreconfirmationStep,
        businessPreconfirmationStep,
        signatoriesPreconfirmationStep({ values }),
        shareholdersPreconfirmationStep({ values }),
        investmentProfilePreconfirmationStep,
        documentationPreconfirmationStep,
      ];
    };

    const handleSendTermsAndConditions = async (
      state: RootState,
      values: FormikValues,
    ) => {
      try {
        const {
          onboardingData: { sendedSteps, formValues },
          activeTemplate: { onboardingType },
        } = state;

        const baseBody = {
          companyType: getCompanyData().companyName,
          personType: onboardingType as PersonType,
          email: formValues?.email,
        };

        const stepSix: StepSixPJ = {
          "@type": "6_JURIDICAL",
          acceptedOptionalTermsAndConditions: values?.senebiAndExteriorNotes,
          acceptedTermsAndConditions: values?.termsAndConditions,
        };

        if (isAgrofinanzasOnboarding() || isAllariaFondosOnboarding()) {
          stepSix.wantsAllariaAccount = !!values?.wantsAllariaAccount;
        }

        // Back reads the FAL terms from the optional field. Intervalores only
        // signs FAL, so its required checkbox goes there.
        if (isAllariaFondosOnboarding()) {
          if (isIntervalores(formValues)) {
            stepSix.acceptedTermsAndConditions = false;
            stepSix.acceptedOptionalTermsAndConditions =
              !!values?.termsAndConditions;
          } else {
            stepSix.acceptedOptionalTermsAndConditions =
              !!values?.acceptedFalTermsAndConditions;
          }
        }

        const stepsWithStepSix = getDraftStepsToSend(sendedSteps, stepSix);

        await sendStep({
          stepId: 6,
          body: {
            ...baseBody,
            steps: stepsWithStepSix,
          },
        });

        const stepsWithStepSeven = getDraftStepsToSend(stepsWithStepSix, {
          "@type": "7_JURIDICAL",
          representatives: [],
        });

        await sendStep({
          stepId: 7,
          body: {
            ...baseBody,
            steps: stepsWithStepSeven,
          },
        });
      } catch (error) {
        console.log(error, "error");
        Sentry.captureException(error);
      }
    };

    const { favicon, formTitle, logo } = getCompanyData();

    return {
      onboardingType: "JURIDICAL",
      formTitle,
      logo,
      icon: juridicalIcon,
      favicon,
      steps: onboardingSteps,
      onLoad: async ({ draftUUID, dispatch, formik, state }) => {
        if (!draftUUID) return;

        const { onAuthorize, onSuccess } = getEmailVerificationHandlers(
          dispatch,
          state,
          formik,
        );
        await onAuthorize(draftUUID);
        await onSuccess();
      },
      remoteSteps: [
        {
          link: "/dni",
          step: identityJuridicalStep(countriesTyped).subSteps?.find(
            ({ label }) => label === "uploadDNI",
          ) as ComponentStep,
        },
        {
          link: "/identidad",
          step: identityJuridicalStep(countries).subSteps?.find(
            ({ label }) => label === "livenessValidation",
          ) as ComponentStep,
        },
      ],
      preconfirmation: ({ values }) => ({
        steps: preconfirmationSteps({ values }).filter(
          Boolean,
        ) as PreconfirmationStep[],
        checkboxes: getCompanyData(values).termsAndConditions.map((term) => ({
          ...term,
          initialValue: false,
          validation: term.required ? checkboxRequired : checkbox,
        })),
        sendTermsAndConditions: handleSendTermsAndConditions,
      }),
    };
  } catch (error) {
    throw new Error(error as string);
  }
};

export default onboardingJuridical;
